import { productIsNewThresholdInDays } from '../../../constants';
import { calculateDifferenceInDays } from '../../../helpers/date';
import { MeilisearchAttribute, MeilisearchProductResource } from '../../@api/Meilisearch';
import { BaseImageResource, VariantReferenceResource } from '../../@api/Sylius';
import { FormImageOption } from '../../@forms/Form/Form';
import { getFilterAttributeKey } from '../../@search/Meilisearch/MeilisearchTransformers';
import { transformMeilisearchBrandNameToLink } from '../../Brand/BrandTransformers';
import { transformToDateIsoString } from '../../Date/DateTransformers';
import { Link } from '../../Link/Link';
import { transformMeilisearchMarketingActionToMarketingAction } from '../../MarketingAction/MarketingActionTransformers';
import { defaultImage, transformImagePathToImage, transformMeilisearchImagesToMediaItems } from '../../Media/MediaTransformers';
import { transformMeilisearchDiscountToDiscount, transformToProductPrice } from '../../Price/PriceTransformers';
import { ProductDefault } from '../ProductDefault/ProductDefault';
import { ProductVariant } from '../ProductVariant/ProductVariant';
import { ProductVariantColor } from '../ProductVariantColor/ProductVariantColor';
import { Product, productVariantSeparator } from './Product';

export const transformSlugToVariantId = (slug: string): string => {
    const parts = slug.split(productVariantSeparator);

    return parts[parts.length - 1];
};

export const transformToProductCode = (product: string): string => product.split('/api/v2/shop/products/')[1];
export const transformToVariantCode = (product: string): string => product.split('/api/v2/shop/product-variants/')[1];

export const transformToProductBreadcrumb = (breadcrumbBases: Link[], resource: Link, index: number): Link => {
    const parentHrefs = breadcrumbBases
        .map(base => base.href)
        .slice(1, index);

    const fullHref = [...parentHrefs, resource.href].join('/');

    return {
        ...resource,
        href: `/${fullHref}`,
    };
};

export const transformToProductImages = (productImages: BaseImageResource[], productVariantImageIds: string[]): BaseImageResource[] => productImages
    .filter(image => productVariantImageIds.includes(image['@id']))
    .sort((image, comparisonImage) => {
        const type = image.type || '';
        const comparisonType = comparisonImage.type || '';

        // Sort images based on type 'image' or 'mood_image'
        return type.localeCompare(comparisonType);
    });

export const transformProductHitToProduct = (resource: MeilisearchProductResource): Product => {
    const brand = resource.brand
        ? transformMeilisearchBrandNameToLink(resource.brand)
        : undefined;

    const mediaItems = resource.main_image
        ? transformMeilisearchImagesToMediaItems(resource)
        : [];

    const createdAtDate = resource.created_at
        ? transformToDateIsoString(resource.created_at)
        : false;

    const isNew = createdAtDate
        ? calculateDifferenceInDays(createdAtDate) <= productIsNewThresholdInDays
        : false;

    const discount = resource.price.discount
        ? transformMeilisearchDiscountToDiscount(resource.price.discount)
        : undefined;

    const price = transformToProductPrice(resource.price.price, resource.price.original_price, discount);

    const marketingAction = resource.marketing_action
        ? transformMeilisearchMarketingActionToMarketingAction(resource.marketing_action)
        : undefined;

    const colorAttributeKey = getFilterAttributeKey(MeilisearchAttribute.color);
    const colorAttribute = resource.attributes[colorAttributeKey];
    const colors = colorAttribute
        ? Object.values(colorAttribute as ProductVariantColor[])
        : [];

    return {
        id: resource.id.toString(),
        legacyId: resource.legacy_id?.toString() || undefined,
        slug: resource.slug,
        name: resource.name,
        productCode: resource.article_code,
        shortDescription: resource.short_description || '',
        description: resource.description || '',
        variantId: resource.id,
        variantCode: resource.code,
        variantsCount: resource.variants || 0,
        brand,
        mediaItems,
        isNew,
        price,
        marketingAction,
        attributes: [],
        dimensionAttributes: [],
        colors,
        relativeUrl: resource.relative_url,
        inStock: resource.in_stock,
        stockQuantity: resource.stock,
        categories: resource.categories,
    };
};

export const transformToProduct = (
    productDefault: ProductDefault,
    productVariant: ProductVariant,
    categories: string[],
): Product => ({
    id: productDefault.id,
    legacyId: productVariant.legacyId,
    slug: productDefault.slug || '',
    relativeUrl: productVariant.relativeUrl,
    name: productVariant.name || '',
    shortDescription: productDefault.shortDescription || '',
    description: productVariant.description || productDefault.description || productDefault.shortDescription || '',
    mediaItems: productDefault.mediaItems,
    isNew: productVariant.isNew,
    price: productVariant.price,
    marketingAction: productVariant.marketingAction,
    attributes: productVariant.attributes,
    dimensionAttributes: productVariant.dimensionAttributes,
    brand: productVariant.brand,
    colors: productVariant.colors,
    variantCode: productVariant.variantCode,
    variantId: productVariant.variantId,
    variants: productDefault.variants,
    inStock: productVariant.inStock,
    stockQuantity: productVariant.stockQuantity,
    categories,
    productCode: productDefault.productCode,
    deliveryNotice: productVariant.deliveryNotice,
});

export const transformToVariantOption = (resource: VariantReferenceResource): FormImageOption => {
    const image = resource.image?.path
        ? transformImagePathToImage(resource.image.path, resource.name)
        : defaultImage();

    return {
        label: resource.name || '',
        value: resource.relativeUrl,
        image,
    };
};
